import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, ChevronLeft, ChevronRight, UserRound } from "lucide-react";
import { listUsers } from "../../services/userService";
import CustomSelect from "../../components/CustomSelect";

const ROLE_BADGE = {
  superadmin:    { label: "Super Admin",    className: "user-role-badge user-role-superadmin" },
  admin:         { label: "Admin",          className: "user-role-badge user-role-admin" },
  project_admin: { label: "Project Admin",  className: "user-role-badge user-role-admin" },
  manager:       { label: "Manager",        className: "user-role-badge user-role-manager" },
};

const AVATAR_COLORS = [
  "var(--primaryColor)",
  "#7c3aed",
  "#059669",
  "#d97706",
  "#dc2626",
  "#0891b2",
  "#be185d",
  "#65a30d",
];

const getInitials = (firstName, lastName) =>
  `${firstName?.[0] ?? ""}${lastName?.[0] ?? ""}`.toUpperCase();

const getAvatarColor = (id) => AVATAR_COLORS[id % AVATAR_COLORS.length];

const UserAvatar = ({ user, size = 44 }) => {
  const [imgError, setImgError] = useState(false);

  if (user.avatar && !imgError) {
    return (
      <img
        src={user.avatar}
        alt={user.firstName}
        onError={() => setImgError(true)}
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          objectFit: "cover",
          border: "2.5px solid #e2e8f0",
          flexShrink: 0,
        }}
      />
    );
  }

  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: getAvatarColor(user.id),
        color: "#ffffff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: size * 0.38,
        fontWeight: 800,
        flexShrink: 0,
        letterSpacing: "0.04em",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
      }}
    >
      {getInitials(user.firstName, user.lastName)}
    </div>
  );
};

const getPaginationItems = (currentPage, totalPages) => {
  if (totalPages <= 7)
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  if (currentPage <= 4) return [1, 2, 3, 4, 5, "end-ellipsis", totalPages];
  if (currentPage >= totalPages - 3)
    return [
      1,
      "start-ellipsis",
      totalPages - 4,
      totalPages - 3,
      totalPages - 2,
      totalPages - 1,
      totalPages,
    ];
  return [
    1,
    "start-ellipsis",
    currentPage - 1,
    currentPage,
    currentPage + 1,
    "end-ellipsis",
    totalPages,
  ];
};


const ITEMS_PER_PAGE = 10;

const Users = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const searchRef = useRef("");
  const searchDebounceRef = useRef(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const fetchUsers = useCallback(async (page, search) => {
    setLoading(true);
    try {
      const res = await listUsers(page, ITEMS_PER_PAGE, search);
      const data = res.data?.response;
      const list = data?.items || data?.users || (Array.isArray(data) ? data : []);
      setUsers(list);
      setTotalCount(data?.pagination?.total || data?.total || list.length);
    } catch {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Page change — read latest search from ref
  useEffect(() => {
    fetchUsers(currentPage, searchRef.current);
  }, [currentPage, fetchUsers]);

  const filtered = roleFilter === 'all'
    ? users
    : users.filter(u => u.role === roleFilter);

  const totalPages = Math.max(1, Math.ceil(totalCount / ITEMS_PER_PAGE));
  const pageStart = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginated = filtered;
  const paginationItems = getPaginationItems(currentPage, totalPages);

  return (
    <main className="app-wrapper url-list-page fill-table-page">
      <div className="container-fluid">
        <section className="url-list-toolbar p-3 mb-3">
          <div className="url-list-actions">
            <div className="url-filter-group">
              <div className="url-search-field">
                <i className="fa-solid fa-magnifying-glass"></i>
                <input
                  type="search"
                  className="form-control"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => {
                    const val = e.target.value;
                    searchRef.current = val;
                    setSearchTerm(val);
                    clearTimeout(searchDebounceRef.current);
                    searchDebounceRef.current = setTimeout(() => {
                      setCurrentPage(1);
                      fetchUsers(1, val);
                    }, 400);
                  }}
                />
              </div>
              <CustomSelect
                value={roleFilter}
                onChange={setRoleFilter}
                options={[
                  { value: "all", label: "All Roles" },
                  { value: "project_admin", label: "Project Admin" },
                  { value: "manager", label: "Manager" },
                ]}
              />
            </div>
            <button
              className="btn btn-primary url-settings-btn"
              onClick={() => navigate("/user-details/new")}
            >
              <Plus size={15} className="me-1" />
              Create User
            </button>
          </div>
        </section>

        <section className="url-table-card">
          <div className="table-responsive">
            <table className="table url-table table-hover align-middle mb-0">
              <colgroup>
                <col style={{ width: 48 }} />
                <col style={{ width: "28%" }} />
                <col className="user-email-col" style={{ width: "35%" }} />
                <col style={{ width: 120 }} />
                <col style={{ width: 80 }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="text-center">#</th>
                  <th>User</th>
                  <th className="user-email-col">Email</th>
                  <th>Role</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="5" className="text-center text-muted py-5">Loading...</td>
                  </tr>
                ) : paginated.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center text-muted py-5">
                      <UserRound
                        size={32}
                        className="mb-2 d-block mx-auto text-secondary"
                      />
                      No users found.
                    </td>
                  </tr>
                ) : null}
                {!loading && paginated.map((user, idx) => {
                  const badge = ROLE_BADGE[user.role] ?? ROLE_BADGE.manager;
                  return (
                    <tr
                      className="history-row"
                      key={user.user_pid || user.userPid || user.id || idx}
                      onClick={() => navigate(`/user-details/${user.user_pid || user.userPid || user.id}`, { state: { user } })}
                    >
                      <td className="url-index text-center">{pageStart + idx + 1}</td>
                      <td>
                        <div className="d-flex align-items-center gap-3">
                          <UserAvatar user={{ ...user, firstName: user.first_name || user.firstName, lastName: user.last_name || user.lastName }} size={28} />
                          <span className="url-index">{user.first_name || user.firstName} {user.last_name || user.lastName}</span>
                        </div>
                      </td>
                      <td className="url-page-title user-email-col">{user.email}</td>
                      <td>
                        <span className={badge.className}>{badge.label}</span>
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <button
                          className="ph-view-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/user-details/${user.user_pid || user.userPid || user.id}`, { state: { user } });
                          }}
                        >
                          View <ChevronRight size={13} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalCount > 0 && (
            <div className="url-pagination-wrap">
              <p className="url-pagination-summary mb-0">
                Showing {pageStart + 1}–
                {Math.min(pageStart + ITEMS_PER_PAGE, totalCount)} of{" "}
                {totalCount}
              </p>
              <nav>
                <ul className="url-pagination mb-0">
                  <li>
                    <button
                      className="url-page-btn"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((p) => p - 1)}
                    >
                      <ChevronLeft size={16} />
                    </button>
                  </li>
                  {paginationItems.map((item, idx) => (
                    <li key={idx}>
                      {typeof item === "string" ? (
                        <span className="url-page-ellipsis">...</span>
                      ) : (
                        <button
                          className={`url-page-btn ${item === currentPage ? "active" : ""}`}
                          onClick={() => setCurrentPage(item)}
                        >
                          {item}
                        </button>
                      )}
                    </li>
                  ))}
                  <li>
                    <button
                      className="url-page-btn"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage((p) => p + 1)}
                    >
                      <ChevronRight size={16} />
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </section>
      </div>
    </main>
  );
};

export default Users;
